import React, { FunctionComponent, useEffect, useState, useCallback, useRef, JSX } from 'react'
import parse, { DOMNode, Element, domToReact } from 'html-react-parser'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

interface PublicationMenuProps {
  navContent: string
  showMenu: boolean
  tocContainerRef: React.RefObject<HTMLDivElement | null>
  HTMLNumbers?: boolean
}

interface MotionUlProps {
  children: React.ReactNode
  isOpen: boolean
  className?: string
}

interface MenuUlProps {
  children: React.ReactNode
  isNested: boolean
  className?: string
  linkText: string
}

const MotionUl: FunctionComponent<MotionUlProps> = ({ children, isOpen, className }) => (
  <motion.ul
    initial={false}
    animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
    transition={{ duration: 0.6, ease: 'easeInOut' }}
    className={className}
    style={{ overflow: 'hidden' }}
  >
    {children}
  </motion.ul>
)

const MenuUl: FunctionComponent<MenuUlProps> = ({ children, isNested, className, linkText }) => {
  const [isOpen, setIsOpen] = useState(!isNested)
  const { t } = useTranslation()

  return (
    <>
      {isNested && (
        <button
          onClick={() => setIsOpen((prev) => !prev)}
          className="expandbtn"
          type="button"
          aria-expanded={isOpen}
          data-label={isOpen ? t('close-sub-menu', { linkText }) : t('open-sub-menu', { linkText })}
        />
      )}
      <MotionUl isOpen={isOpen} className={className}>
        {children}
      </MotionUl>
    </>
  )
}

// Utility functions
const decodeEntities = (encodedString: string): string => {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = encodedString
  return textarea.value
}

const hasNumber = (str: string): boolean => /\d/.test(str)
const isNumeric = (str: string): boolean => !isNaN(parseFloat(str))

const parseMenu = (html: string) => {
  let isFirstUl = true

  const replaceFn = (domNode: DOMNode): JSX.Element | void => {
    if (!(domNode instanceof Element)) return

    if (domNode.name === 'ul') {
      const className = domNode.attribs?.class || ''
      if (isFirstUl) {
        isFirstUl = false
        return <ul className={className}>{domToReact(domNode.children as DOMNode[], { replace: replaceFn })}</ul>
      }

      const linkElement = domNode.parent?.children.find(
        (child) => child instanceof Element && child.tagName === 'a',
      ) as Element | undefined
      const linkText = linkElement?.children.find((child) => child.type === 'text')?.data || ''

      return (
        <MenuUl isNested={true} className={className} linkText={linkText}>
          {domToReact(domNode.children as DOMNode[], { replace: replaceFn })}
        </MenuUl>
      )
    }

    if (domNode.name === 'li') {
      const hasNestedUl = domNode.children.some((child) => child instanceof Element && child.name === 'ul')
      if (hasNestedUl) {
        return <li className="expand">{domToReact(domNode.children as DOMNode[], { replace: replaceFn })}</li>
      }
    }
  }

  return parse(html, { replace: replaceFn })
}

const PublicationMenu: FunctionComponent<PublicationMenuProps> = ({
  navContent,
  showMenu,
  tocContainerRef,
  HTMLNumbers = false,
}) => {
  const [activeId, setActiveId] = useState<string>('')
  const [sections, setSections] = useState<{ id: string; offsetTop: number }[]>([])
  const isScrollingRef = useRef(false)

  // Collect sections based on menu links
  const collectSections = useCallback(() => {
    const tocContainer = tocContainerRef.current
    if (!tocContainer) return

    const anchorLinks = tocContainer.querySelectorAll<HTMLAnchorElement>('a[href^="#"]')
    const sectionData = Array.from(anchorLinks)
      .map((link) => {
        const href = link.getAttribute('href') || ''
        const targetId = href.substring(1)
        const targetElement = document.getElementById(targetId)

        if (targetElement) {
          return { id: targetId, offsetTop: targetElement.offsetTop }
        }
        return null
      })
      .filter((data): data is { id: string; offsetTop: number } => data !== null)

    // Sort sections by offsetTop to ensure correct order
    sectionData.sort((a, b) => a.offsetTop - b.offsetTop)

    setSections(sectionData)

    // Set the first link as active initially if sections exist and no activeId is set
    if (sectionData.length > 0 && !activeId) {
      setActiveId(sectionData[0].id)
    }
  }, [tocContainerRef, activeId])

  // Update the active link in the menu, including all parent links
  const updateActiveLink = useCallback(
    (id: string) => {
      const menuElement = tocContainerRef.current
      if (!menuElement || !id) return

      // Remove existing active classes from all <a> elements
      menuElement.querySelectorAll('a.active').forEach((el) => el.classList.remove('active'))

      // Find the current active link based on the provided id
      const activeLink = menuElement.querySelector(`a[href="#${id}"]`) as HTMLAnchorElement
      if (!activeLink) return

      // Add active class to the current link, even if it’s in a collapsed sub-menu
      activeLink.classList.add('active')

      // Traverse up the hierarchy and add active class to all parent links
      let currentLi = activeLink.closest('li')
      while (currentLi) {
        const parentLi = currentLi.parentElement?.closest('li')
        if (parentLi) {
          const parentLink = parentLi.querySelector(':scope > a[href^="#"]') as HTMLAnchorElement
          if (parentLink) {
            // Add active class to parent link, regardless of sub-menu visibility
            parentLink.classList.add('active')
          }
          currentLi = parentLi
        } else {
          break
        }
      }
    },
    [tocContainerRef],
  )

  // Handle clicks on menu links
  const handleClickOnAnchor = useCallback(
    (event: MouseEvent) => {
      const anchor = event.target as HTMLAnchorElement
      const href = anchor?.getAttribute('href')
      if (!href?.startsWith('#') || anchor.classList.contains('js-scroll-trigger')) return

      event.preventDefault()
      const targetId = href.substring(1)
      const targetElement = document.getElementById(targetId)

      if (targetElement) {
        isScrollingRef.current = true
        const headerOffset = 50
        const scrollPosition = targetElement.offsetTop - headerOffset

        window.scrollTo({
          top: scrollPosition,
          behavior: 'smooth',
        })

        setActiveId(targetId)
        updateActiveLink(targetId)

        // Reset scrolling flag after animation
        setTimeout(() => {
          isScrollingRef.current = false
        }, 1000) // Adjust if smooth scroll duration differs
      }
    },
    [updateActiveLink],
  )

  // Handle scroll updates
  const handleScrollUpdate = useCallback(() => {
    if (isScrollingRef.current) return

    if (sections.length === 0) return

    if (!activeId) {
      setActiveId(sections[0].id)
      return
    }

    const scrollY = window.scrollY
    const currentIndex = sections.findIndex((section) => section.id === activeId)

    if (currentIndex === -1) {
      setActiveId(sections[0].id)
      return
    }

    const currentSection = sections[currentIndex]
    const nextSection = sections[currentIndex + 1]

    if (scrollY < sections[0].offsetTop - 50) {
      if (activeId !== sections[0].id) {
        setActiveId(sections[0].id)
      }
      return
    }

    if (nextSection && scrollY >= nextSection.offsetTop - 50) {
      setActiveId(nextSection.id)
    } else if (scrollY < currentSection.offsetTop - 50 && currentIndex > 0) {
      setActiveId(sections[currentIndex - 1].id)
    }
  }, [sections, activeId])

  // Process anchors (e.g., remove numbers if needed)
  const processAnchors = useCallback(() => {
    const container = tocContainerRef.current
    if (!container) return

    const anchorElements = container.querySelectorAll<HTMLAnchorElement>('a[href*="#_"]')
    anchorElements.forEach((el) => {
      let text = decodeEntities(el.textContent || '').replace('\t', ' ')
      if (!hasNumber(text.charAt(0))) el.classList.add('nocount')

      let parts = text.split(' ')
      if (!HTMLNumbers && parts.length > 0 && isNumeric(parts[0])) {
        parts.shift()
      }
      el.textContent = parts.join(' ')
    })
  }, [HTMLNumbers, tocContainerRef])

  // Collect sections when TOC content changes
  useEffect(() => {
    collectSections()
  }, [navContent, tocContainerRef, collectSections])

  // Set up event listeners and initial state
  useEffect(() => {
    processAnchors()
    document.addEventListener('click', handleClickOnAnchor)
    window.addEventListener('scroll', handleScrollUpdate)
    handleScrollUpdate() // Set initial active link

    return () => {
      document.removeEventListener('click', handleClickOnAnchor)
      window.removeEventListener('scroll', handleScrollUpdate)
    }
  }, [processAnchors, handleClickOnAnchor, handleScrollUpdate])

  // Update active link whenever activeId changes
  useEffect(() => {
    if (activeId) {
      updateActiveLink(activeId)
    }
  }, [activeId, updateActiveLink])

  return (
    <nav
      id="pub-toc"
      className="pubnav list-group list-group-flush"
      aria-label="Publikationens innehållsförteckning"
      style={{ display: showMenu ? 'flex' : 'none' }}
    >
      <div className="toc-container" ref={tocContainerRef}>
        {parseMenu(navContent)}
      </div>
    </nav>
  )
}

export default PublicationMenu
